import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { animalCollectionData, flexItemsData, MYSTERY_BOXES, miners, MYSTERY_BOXES_PRIZES, BIGCOINTOKEN_ADDRESS, MISTERY_BOX_ADDRESS } from '../../utils/constants';
import Text from '../../components/Text';
import openingGif from '../../assets/img/mystery/opening.gif';
import animal13 from '../../assets/img/animals/gifs/alpaca.gif';
import { BigNumber, ethers } from 'ethers';
import { getMysteryBoxContract } from '../MysteryBoxPage/MysteryBoxPage';
import { notifyError, notifySuccess } from '../../hooks/utils';
import { useAccount, useReadContract, useWaitForTransactionReceipt, useWriteContract } from 'wagmi';
import { erc20Abi, parseUnits, zeroAddress } from 'viem';

interface Miner {
    id: number;
    name: string;
    img: string;
    gif: string;
    hashRate: number;
    price: number;
    energy: number;
    description: string;
    availableToBuy: boolean;
    isFlex: boolean;
}

interface ContractEvent extends ethers.Event {
    event: string;
    args: any;
}

const PopupOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const PopupContent = styled.div`
    background: #fff;
    padding: 2rem;
    border-radius: 8px;
    max-width: 800px;
    width: 90%;
    max-height: 80vh;
    overflow-y: auto;
    position: relative;
`;

const CloseButton = styled.button`
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: #507c83;
`;

const Title = styled.h2`
    color: #507c83;
    font-size: 2rem;
    margin-bottom: 2rem;
    text-align: center;
    font-family: "Press Start 2P", monospace;
`;

const Subtitle = styled.p`
    color: #507c83;
    font-size: 1rem;
    margin-bottom: 2rem;
    text-align: center;
    font-family: "Press Start 2P", monospace;
    line-height: 1.5;
    opacity: 0.8;
`;

const ItemRow = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 1rem;
    padding: 0.5rem;
    border: 2px solid #507c83;
    border-radius: 4px;
`;

const ItemImage = styled.img`
    width: 64px;
    height: 64px;
    margin-right: 1rem;
    object-fit: contain;
`;

const ItemName = styled.span`
    font-family: "Press Start 2P", monospace;
    color: #507c83;
    margin-right: 1rem;
    min-width: 150px;
`;

const ProgressBar = styled.div`
    flex: 1;
    height: 20px;
    background: #e0e0e0;
    border-radius: 10px;
    overflow: hidden;
    position: relative;
`;

const ProgressFill = styled.div<{ width: number }>`
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: ${props => props.width}%;
    background: #507c83;
    transition: width 0.3s ease-in-out;
`;

const ProbabilityText = styled.span`
    font-family: "Press Start 2P", monospace;
    color: #507c83;
    margin-left: 1rem;
    min-width: 80px;
    text-align: right;
`;

const Price = styled(Text)`
    font-size: 1.3em;
    color: #fff !important;
    font-family: "Press Start 2P", monospace;
    margin-bottom: 1.5em;
    margin-top: 0.2em;
`;

interface MysteryBoxPopupProps {
    type: 'animals' | 'flexshop' | 'mix';
    onClose: () => void;
    isOpenBox?: boolean;
    minerId?: number | null;
    onMinerWon?: (minerId: number | null) => void;
    boxId: number;
}

const MysteryBoxPopup: React.FC<MysteryBoxPopupProps> = ({ type, onClose, isOpenBox = false, minerId, onMinerWon, boxId }) => {
    const [boxStage, setBoxStage] = useState<'initial' | 'spinning' | 'open_box' | 'gif' | 'opened'>('initial');
    const [isShaking, setIsShaking] = useState(true);
    const [showPrize, setShowPrize] = useState(false);

    useEffect(() => {
        if (boxStage !== 'initial') return;
        let shake = true;
        let timeout: ReturnType<typeof setTimeout>;
        function loop() {
            setIsShaking(shake);
            timeout = setTimeout(() => {
                shake = !shake;
                loop();
            }, shake ? 3000 : 1000);
        }
        loop();
        return () => clearTimeout(timeout);
    }, [boxStage]);

    useEffect(() => {
        if (boxStage === 'opened') {
            const timer = setTimeout(() => setShowPrize(true), 1000);
            return () => clearTimeout(timer);
        } else {
            setShowPrize(false);
        }
    }, [boxStage]);


    const account = useAccount();

    const { data: hash, error, isPending, writeContract } = useWriteContract();
    const { isLoading: isConfirming, isSuccess: isConfirmed } = useWaitForTransactionReceipt({
        hash,
    });

    const [txType, setTxType] = useState<"approve" | "buy" | null>(null);


    // Read the allowance and refetch on demand
    const { data: allowance, refetch: refetchAllowance } = useReadContract({
        abi: erc20Abi,
        address: BIGCOINTOKEN_ADDRESS,
        functionName: "allowance",
        args: [account ? account.address as any : zeroAddress, MISTERY_BOX_ADDRESS],
    });

    const [hasSufficientAllowance, setHasSufficientAllowance] = useState(false);
    const [isApproving, setApproving] = useState(false);

    const handleApproveTokens = async () => {
        if (!account) {
            // Redirect to login or show a message
            notifyError("You need to connect wallet first.");
            return;
        }

        try {
            setTxType("approve");
            setApproving(true);

            const transactionArgs = {
                abi: erc20Abi,
                address: BIGCOINTOKEN_ADDRESS,
                functionName: 'approve',
                args: [MISTERY_BOX_ADDRESS, parseUnits(MYSTERY_BOXES[boxId-1].price.toString(), 18)],
            };

            writeContract(transactionArgs as any);
        } catch (error) {
            notifyError("An error occurred while trying to approve.");
        } finally {
            setApproving(false);
        }
    };

      useEffect(() => {
        if (allowance && boxId) {
          const isAllowed = BigNumber.from(allowance).gte(parseUnits(MYSTERY_BOXES[boxId-1].price.toString(), 18));
          setHasSufficientAllowance(isAllowed);
        } else {
          setHasSufficientAllowance(false); // Reset when no allowance or trade value
        }
      }, [allowance, boxId]); // Update hasSufficientAllowance when allowance or trade value changes
    
      useEffect(() => {
        // Refetch allowance automatically when input trade value changes
        refetchAllowance();
      }, [boxId]); // Optionally refetch allowance when input value changes
    
      useEffect(() => {
        // Refetch allowance when the transaction is confirmed
        if (isConfirmed) {
          refetchAllowance();
        }
      }, [isConfirmed, refetchAllowance]);
      useEffect(() => {
        if (hash) {
          switch (txType) {
            case "approve":
              console.log("Transaction hash:", hash);
              notifySuccess("Approval transaction sent.");
              break;
            case "buy":
              console.log("Transaction hash:", hash);
              notifySuccess("Buy transaction sent.");
              break;
            default:
              console.log("Transaction hash:", hash);
              notifySuccess("Transaction sent.");
          }
        }
        if (error) {
          switch (txType) {
            case "approve":
              console.error("Error during contract write:", error);
              notifyError("Failed to approve.");
              break;
            case "buy":
              console.error("Error during contract write:", error);
              notifyError("Failed to buy item.");
              break;
            default:
              console.error("Error during contract write:", error);
              notifyError("Transaction failed.");
          }
        }
        if (isConfirmed) {
          switch (txType) {
            case "approve":
              notifySuccess("Approval successful.");
              break;
            case "buy":
              notifySuccess("Item bought!");
              break;
            default:
              notifySuccess("Transaction confirmed.");
          }
    
          // Optional: Reset txType after confirmation
          setTxType(null);
        }
      }, [hash, error, isConfirmed]);
    

    const handleBoxClick = async () => {
        if (boxStage !== 'initial') return;

        try {
            if (!(window as any).ethereum) throw new Error("No wallet found");
            await (window as any).ethereum.request({ method: "eth_requestAccounts" });
            const provider = new ethers.providers.Web3Provider((window as any).ethereum);
            const signer = provider.getSigner();
            const contract = getMysteryBoxContract(signer);
            const userAddress = await signer.getAddress();

            // Get the number of boxes before transaction
            const boxesBoughtBefore = await contract.userBoxesBought(userAddress);

            setBoxStage('spinning');
            setIsShaking(false);

            // Buy the box
            const tx = await contract.buyMysteryBox(boxId);
            const receipt = await tx.wait();

            // Show open box first
            setBoxStage('open_box');

            // Wait for 1 second before showing the opening gif
            setTimeout(() => {
                setBoxStage('gif');
            }, 1000);

            // Get the number of boxes after transaction
            const boxesBoughtAfter = await contract.userBoxesBought(userAddress);

            // Get the pending box using the box count
            const pendingBox = await contract.pendingBoxes(boxesBoughtAfter.toNumber() - 1);

            // Get the prizeWonId from the pending box
            const prizeWonId = pendingBox.prizeWonId;

            // Set the miner ID and update the stage
            onMinerWon?.(prizeWonId.toNumber());

            // Wait a bit before showing the result
            setTimeout(() => {
                setBoxStage('opened');
            }, 1500); // Show gif for 1.5s

        } catch (err) {
            setBoxStage('initial');
            notifyError("Error: " + (err as any).message);
        }
    };

    const getTitle = () => {
        if (!isOpenBox) {
            switch (type) {
                case 'animals':
                    return 'Animals Mystery Box Contents';
                case 'flexshop':
                    return 'FlexShop Mystery Box Contents';
                case 'mix':
                    return 'Mix Mystery Box Contents';
                default:
                    return 'Mystery Box Contents';
            }
        }
        switch (boxStage) {
            case 'initial':
                return 'Click to Open';
            case 'spinning':
                return 'Opening Mystery Box...';
            case 'open_box':
                return 'Box Opening...';
            case 'gif':
                return 'Opening Mystery Box...';
            case 'opened':
                if (showPrize && minerId) {
                    const miner = miners.find((m: Miner) => m.id === minerId);
                    return `You got ${miner?.name}!`;
                }
                return 'Box Opened!';
            default:
                return '';
        }
    };

    const getSubtitle = () => {
        if (!isOpenBox) {
            switch (type) {
                case 'animals':
                    return 'The progress bar shows your chance of getting each animal. Higher percentage means higher chance!';
                case 'flexshop':
                    return 'The progress bar shows your chance of getting each flex item. Higher percentage means higher chance!';
                case 'mix':
                    return 'The progress bar shows your chance of getting each item. This box contains both animals and flex items with equal chances!';
                default:
                    return '';
            }
        }
        switch (boxStage) {
            case 'initial':
                return 'Click on the box to reveal your prize';
            case 'spinning':
                return 'Your mystery box is being opened...';
            case 'open_box':
                return 'Your mystery box is opening...';
            case 'gif':
                return 'Your mystery box is being opened...';
            case 'opened':
                return 'Congratulations! You got a prize!';
            default:
                return '';
        }
    };

    const renderItems = () => {
        if (isOpenBox) {
            const box = MYSTERY_BOXES.find(box => box.type === type);
            return (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                }}>
                    <div
                        style={{
                            width: '300px',
                            height: '300px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            animation: boxStage === 'spinning' ? 'spin 2s linear infinite' : 'none',
                            cursor: boxStage === 'initial' ? 'pointer' : 'default',
                        }}
                        onClick={hasSufficientAllowance ? handleBoxClick : handleApproveTokens}
                    >
                        {boxStage === 'open_box' ? (
                            <img
                                src={box?.openImage}
                                alt="Open box"
                                style={{
                                    width: '200px',
                                    height: '200px',
                                    objectFit: 'contain',
                                }}
                            />
                        ) : boxStage === 'gif' ? (
                            <img
                                src={openingGif}
                                alt="Opening animation"
                                style={{
                                    width: '300px',
                                    height: '300px',
                                    objectFit: 'contain',
                                }}
                            />
                        ) : boxStage === 'opened' && !showPrize ? (
                            <img
                                src={openingGif}
                                alt="Opening animation"
                                style={{
                                    width: '300px',
                                    height: '300px',
                                    objectFit: 'contain',
                                }}
                            />
                        ) : boxStage === 'opened' && showPrize ? (
                            minerId ? (
                                <img
                                    src={miners.find((m: Miner) => m.id === minerId + 1)?.gif}
                                    alt={miners.find((m: Miner) => m.id === minerId + 1)?.name}
                                    className="prize-animate"
                                    style={{
                                        width: '220px',
                                        height: '220px',
                                        objectFit: 'contain',
                                        transition: 'all 0.7s cubic-bezier(0.4,0,0.2,1)'
                                    }}
                                />
                            ) : (
                                <div>No prize found</div>
                            )
                        ) : (
                            <img
                                src={box?.image}
                                alt="Mystery box"
                                className={boxStage === 'initial' ? (isShaking ? 'shake' : 'shake-paused') : ''}
                                style={{
                                    width: '200px',
                                    height: '200px',
                                    objectFit: 'contain',
                                    transition: 'transform 0.2s',
                                }}
                            />
                        )}
                    </div>
                    <Text
                        as="p"
                        className="text-[20px] font-normal font-upheavalttbrk font-upheaval"
                        style={{ color: '#507c83', fontSize: '1.2em', margin: '2rem 0 0 0' }}
                    >
                        {boxStage === 'initial' ? 'Click on the box to open it' :
                            boxStage === 'spinning' ? 'Opening your ' + type + ' mystery box...' :
                                boxStage === 'open_box' ? 'Opening your ' + type + ' mystery box...' :
                                    boxStage === 'gif' ? 'Opening your ' + type + ' mystery box...' :
                                        showPrize && minerId ? `You got the ${miners.find((m: Miner) => m.id === minerId)?.name}!` :
                                            'Your prize is ready!'}
                    </Text>
                </div>
            );
        }

        switch (type) {
            case 'animals':
                return MYSTERY_BOXES[0].prizes.map((item, index) => (
                    <ItemRow key={MYSTERY_BOXES_PRIZES[item].id}>
                        <ItemImage src={MYSTERY_BOXES_PRIZES[item].img} alt={MYSTERY_BOXES_PRIZES[item].name} />
                        <ItemName>{MYSTERY_BOXES_PRIZES[item].name}</ItemName>
                        <ProgressBar>
                            <ProgressFill width={(MYSTERY_BOXES[0].probabilities[index] / 10000000) * 100} />
                        </ProgressBar>
                        <ProbabilityText>{((MYSTERY_BOXES[0].probabilities[index] / 10000000) * 100).toLocaleString("en-US")}%</ProbabilityText>
                    </ItemRow>
                ));
            case 'flexshop':
                return MYSTERY_BOXES[1].prizes.map((item, index) => (
                    <ItemRow key={MYSTERY_BOXES_PRIZES[item].id}>
                        <ItemImage src={MYSTERY_BOXES_PRIZES[item].img} alt={MYSTERY_BOXES_PRIZES[item].name} />
                        <ItemName>{MYSTERY_BOXES_PRIZES[item].name}</ItemName>
                        <ProgressBar>
                            <ProgressFill width={(MYSTERY_BOXES[1].probabilities[index] / 10000000) * 100} />
                        </ProgressBar>
                        <ProbabilityText>{((MYSTERY_BOXES[1].probabilities[index] / 10000000) * 100).toLocaleString("en-US")}%</ProbabilityText>
                    </ItemRow>
                ));
            case 'mix':
                return MYSTERY_BOXES[2].prizes.map((item, index) => (
                    <ItemRow key={MYSTERY_BOXES_PRIZES[item].id}>
                        <ItemImage src={MYSTERY_BOXES_PRIZES[item].img} alt={MYSTERY_BOXES_PRIZES[item].name} />
                        <ItemName>{MYSTERY_BOXES_PRIZES[item].name}</ItemName>
                        <ProgressBar>
                            <ProgressFill width={(MYSTERY_BOXES[2].probabilities[index] / 10000000) * 100} />
                        </ProgressBar>
                        <ProbabilityText>{((MYSTERY_BOXES[2].probabilities[index] / 10000000) * 100).toLocaleString("en-US")}%</ProbabilityText>
                    </ItemRow>
                ));
            default:
                return null;
        }
    };

    return (
        <PopupOverlay onClick={onClose}>
            <PopupContent onClick={e => e.stopPropagation()}>
                <CloseButton onClick={onClose}>×</CloseButton>
                <Title>{getTitle()}</Title>
                <Subtitle>{getSubtitle()}</Subtitle>
                {renderItems()}
                <Price
                    as="p"
                    className="text-[18px] font-normal font-upheaval text-center"
                >
                    PRICE: ...
                </Price>
            </PopupContent>
        </PopupOverlay>
    );
};

export default MysteryBoxPopup; 